import axios from "axios";
import { useEffect, useState } from "react";
import { animated, config, useSpring } from "react-spring";
import { useLocation } from "wouter";
import { colors, locationsToScreens, screens } from "../constants/constants";
import { useWindowSize } from "../hooks/useWindowSize";
import { OnCurtainStart } from "../types/types";
import { Contour } from "./ScreenLanding/components/Contour";
import { Lines } from "./ScreenLanding/components/Lines";
import { SVGCircle } from "./ScreenMenu/components/SVGCircle";
import { ScreenBackground } from "./shared/ScreenBackground";
import {
  AnimatedDNAStrands,
  AnimatedMedia,
  AnimatedPerf,
  AnimatedPreorder,
} from "./shared/SVGComponents";
import { chromeVideoName, safariVideoName } from "./shared/Video";

const SlidesAndLoader = ({
  downloadProgress,
}: {
  downloadProgress: number;
}) => {
  // spring for loading bar
  const { progressWidth } = useSpring({
    to: { progressWidth: `${downloadProgress}%` },
  });

  const [slideMultiplier, slideMultiplierSet] = useState(0);

  const { slide } = useSpring({
    from: { slide: `-${slideMultiplier * 150}px` },
    to: { slide: `-${(slideMultiplier + 1) * 150}px` },
    config: config.molasses,
    // config: { duration: 2000 },
    delay: 2200,
    onRest: () => {
      if (slideMultiplier <= 1) {
        slideMultiplierSet(slideMultiplier + 1);
      }
      if (slideMultiplier === 2) {
        slideMultiplierSet(-1);
      }
    },
  });
  const { w } = useWindowSize();
  return (
    <div
      id="SlidesAndLoader"
      {...{
        style: {
          gridArea: "AppContentContainer",
          placeSelf: "center",
          width: "300px",
          height: "300px",
          display: "grid",
          gridTemplate: `
              "animation"
                "title"
                "bar" 
            `,
          gap: w(1),
          placeItems: "center",
          // border
          borderRadius: "0.75rem",
          borderWidth: "8px",
          borderColor: "rgba(229, 231, 235, 0.05)",
          borderStyle: "solid",
          padding: w(2),
          background: "#41286A",
          zIndex: 9999, // curtain is 5 nines
        },
      }}
    >
      <div
        id="slider-animation-container"
        {...{
          style: {
            display: "grid",
            gridArea: "animation",
            width: "150px",
            overflow: "hidden",
          },
        }}
      >
        <animated.div
          id="slider-animation-sliding"
          {...{
            style: {
              width: "150px",
              display: "grid",
              gridTemplate: `
              "a b c d"
            `,
              marginLeft: slide,
            },
          }}
        >
          <SVGCircle
            {...{
              active: true,
              style: {
                width: "150px",
                height: "150px",
                margin: "auto",
                gridArea: "a",
              },
              svgComponent: <AnimatedDNAStrands {...{ active: false }} />,
            }}
          />
          <SVGCircle
            {...{
              active: true,
              style: {
                width: "150px",
                height: "150px",
                margin: "auto",
                gridArea: "b",
              },
              svgComponent: <AnimatedPerf {...{ active: false }} />,
            }}
          />
          <SVGCircle
            {...{
              active: true,
              style: {
                width: "150px",
                height: "150px",
                margin: "auto",
                gridArea: "b",
              },
              svgComponent: <AnimatedMedia {...{ active: false }} />,
            }}
          />
          <SVGCircle
            {...{
              active: true,
              style: {
                width: "150px",
                height: "150px",
                margin: "auto",
                gridArea: "b",
              },
              svgComponent: <AnimatedPreorder {...{ active: false }} />,
            }}
          />
        </animated.div>
      </div>

      <div
        id=""
        {...{
          style: {
            gridArea: "title",
            textAlign: "center",
            display: "grid",
            placeItems: "center",
          },
        }}
      >
        <div id="" {...{ style: {} }}>
          Loading bio-computer model...
        </div>
      </div>
      <div
        id=""
        {...{
          style: {
            gridArea: "bar",
            width: "100%",
            display: "grid",
            gridTemplate: `
                    "center"
                `,
            placeSelf: "end center",
          },
        }}
      >
        <div
          id="container-bar"
          {...{
            style: {
              gridArea: "center",
              height: "10px",
              width: "100%",
              background: "#573C85",
              borderRadius: "2px",
            },
          }}
        ></div>
        <animated.div
          id="progress-bar"
          {...{
            style: {
              gridArea: "center",
              height: "10px",
              width: progressWidth,
              background: "#CF64B8",
              borderRadius: "2px",
              placeSelf: "center start",
            },
          }}
        ></animated.div>
      </div>
    </div>
  );
};

const isSafariBrowser =
  navigator.userAgent.indexOf("Safari") > -1 &&
  navigator.userAgent.indexOf("Chrome") <= -1;

const ScreenLoadingAssets = ({
  onCurtainStart,
}: // loadingSet,
{
  onCurtainStart: OnCurtainStart;
}) => {
  // console.log("ScreenLoadingAssets renders");

  const [location] = useLocation();

  const [downloadProgress, downloadProgressSet] = useState(0);
  // console.log({ downloadProgress });

  useEffect(() => {
    const indexedDB = window.indexedDB;

    // function definition (called later)
    const saveVideoToIndexedDB = (blob: any) => {
      const request = indexedDB.open("iocodb", 1);

      request.onerror = function (event) {
        console.error("An error occurred with IndexedDB");
        console.error(event);
      };

      // establish schema for creation and update of database
      request.onupgradeneeded = function () {
        const db = request.result;
        const store = db.createObjectStore("video", { keyPath: "id" });
        store.createIndex("video_name", ["name"]);
      };

      // write video to database
      request.onsuccess = function () {
        // console.log("Database opened successfully");
        const db = request.result;
        const transaction = db.transaction("video", "readwrite");
        const store = transaction.objectStore("video");
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const video = {
          id: 1,
          name: "video",
          data: blob,
        };
        store.put(video);

        // exit load screen
        onCurtainStart({ curtainTo: locationsToScreens(location) });
      };
    };

    let abortController = new AbortController();
    let signal = abortController.signal;
    axios
      .get(isSafariBrowser ? safariVideoName : chromeVideoName, {
        responseType: "blob",
        onDownloadProgress: (progressEvent) => {
          // console.log({progressEvent});

          let percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          downloadProgressSet(percentCompleted); // you can use this to show user percentage of file downloaded
          // console.log({percentCompleted});
        },
        signal: signal,
      })
      .then((blob) => {
        // console.log({blob});
        saveVideoToIndexedDB(blob.data);
      });

    return () => {
      abortController.abort();
    };
  }, [location, onCurtainStart]);

  const background = `
  radial-gradient(circle at 50% 0%, #9758E455 0, transparent 50%),
  radial-gradient(circle at 50% 0%, #4C2C71BB 0, transparent 90%),
  linear-gradient(#35225A, #190E29)
  `;

  const { horizontal } = useWindowSize();

  return (
    <>
      <ScreenBackground
        {...{
          background,
          screenName: screens.preorder,
          gridArea: "AppContentContainer",
        }}
      />
      <Lines
        {...{
          gridArea: "AppContentContainer",
          noise: true,
          numberOfDotsPerLines: horizontal ? 20 : 16,
          range: 10,
          numberOfLines: horizontal ? 30 : 16,
          timeIntervalMilliseconds: 2500,
          timeRange: 500,
          containerStyle: {
            height: "120%",
            marginTop: "-10%",
          },
          dotStyle: {
            background: "#BD58AA",
            height: "4px",
            width: "2px",
            transform: "translate(-0.5px) rotate(45deg)",
            borderRadius: "1px",
          },
        }}
      />
      {/* <Contour {...{ gridArea: "AppContentContainer" }} /> */}
      <SlidesAndLoader {...{ downloadProgress }} />
    </>
  );
};

export { ScreenLoadingAssets };
